import React, { useCallback, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import useSWR, { mutate } from "swr";

import useUser from "@/components/useUser";
import ExternalLink from "../ExternalLink";
import { IonIcon } from "../Icons.js";
import Image from "next/image";
import LoginProtectedButton from "../LoginProtectedButton";
import { Modal } from "../Modal";
import { useProjectData } from "../ProjectDataContext";
import GetInvolvedModal from "./GetInvolvedModal";
import { retrieveCSRFToken } from "@/src/utils/ajax";

const ProjectSupportButton = ({
  user,
  isSupporting,
  handleSupportTheProject,
}) => {
  const [showModal, setShowModal] = useState(false);
  const router = useRouter();

  const currentUrl = router.asPath;

  if (user) {
    return (
      <button
        className={`button is-info ${isSupporting ? "" : "is-outlined"}`}
        onClick={handleSupportTheProject}
      >
        <IonIcon name="thumbs-up" size="small" />
        <span className="pl-1">{"Support"}</span>
      </button>
    );
  }

  return (
    <>
      <Modal showModal={showModal} closeModal={() => setShowModal(false)}>
        <div>
          <h2 className="subtitle has-text-centered">
            You must be signed in to support a project.
          </h2>

          <div className="buttons is-right">
            <Link
              href={`/signup?next=${currentUrl}`}
              className="button is-warning"
            >
              Sign Up
            </Link>
            <Link
              href={`/login?next=${currentUrl}`}
              className="button is-light"
            >
              Log In
            </Link>
          </div>
        </div>
      </Modal>

      <button
        className="button is-info is-outlined"
        onClick={() => setShowModal(true)}
      >
        <IonIcon name="thumbs-up" size="small" />
        <span className="pl-1">{"Support"}</span>
      </button>
    </>
  );
};

const unLikeProject = (projectId) => {
  const url = `/api/projects/${projectId}/unlike/`;
  return fetch(url, {
    method: "POST",
    headers: {
      "X-CSRFToken": retrieveCSRFToken(),
      "Content-Type": "application/json",
    },
  }).then((response) => response.json());
};

const likeProject = (projectId) => {
  const url = `/api/projects/${projectId}/like/`;
  return fetch(url, {
    method: "POST",
    headers: {
      "X-CSRFToken": retrieveCSRFToken(),
      "Content-Type": "application/json",
    },
  }).then((response) => response.json());
};

const ProjectDetailsCard = ({
  children,
  projectId,
  _projectSupporters,
  _supportTheProject,
  tabRoutes,
}) => {
  const { data: project } = useSWR(`/api/projects/${projectId}/`);

  const { data: projectSupporters } = useSWR(
    `/api/projects/${projectId}/likes/`
  );

  const router = useRouter();

  const { user, isLoading: isUserLoading } = useUser();

  const showGetInvolvedModal = () => setShowModal(true);
  const isCreatedByCurrentUser = project.created_by_user?.id === user?.id;

  const isSupporting = projectSupporters?.liked_by_you;
  const supporterCount = projectSupporters?.likes;

  const {
    projectNeeds: validSeekingOptions,
    projectNeedsMap: validSeekingOptionMap,
    projectStatusMap: statusMap,
  } = useProjectData();

  const showSampleProjectBadge = !!project.is_sample_project;
  const showGetInvolvedButton =
    !isUserLoading && !isCreatedByCurrentUser && !showSampleProjectBadge;

  const searchParams = new URLSearchParams(router.query);
  const [showModal, setShowModal] = useState(
    user && showGetInvolvedButton && !!searchParams.get("showGetInvolvedModal")
  );

  const supportTheProject = useCallback(
    (like) => {
      mutate(
        `/api/projects/${projectId}/likes/`,
        like ? likeProject(projectId) : unLikeProject(projectId),
        {
          // optimisticData: ehhhh its fine if we don't
          revalidate: false,
          populateCache: true,
          rollbackOnError: true,
        }
      );
    },
    [projectId]
  );

  const handleSupportTheProject = () => {
    if (!user?.id) {
      return;
    }

    supportTheProject(!isSupporting);
  };

  return (
    <div className="container">
      {user && (
        <GetInvolvedModal
          user={user}
          project={project}
          showModal={showModal}
          setShowModal={setShowModal}
          validSeekingOptions={validSeekingOptions}
          validSeekingOptionMap={validSeekingOptionMap}
        />
      )}

      <div className="columns">
        <div className="column is-one-third">
          <div className="card">
            {project.main_image_link && (
              <div className="card-image">
                <figure className="image is-4by3">
                  <Image
                    src={project.main_image_link}
                    layout="fill"
                    alt="project main"
                  />
                </figure>
              </div>
            )}

            <div className="card-content">
              <div className="media-content">
                <p>
                  <strong>{project.name}</strong>
                </p>
                {project.external_url && (
                  <p className="block">
                    <ExternalLink url={project.external_url} newPage>
                      <span>Website Link</span>
                      <span className="icon">
                        <IonIcon name="open" size="small" />
                      </span>
                    </ExternalLink>
                  </p>
                )}
                <p>Status: {statusMap[project.status]}</p>
                <p>
                  {"Seeking: "}
                  <span className="tags">
                    {project.seeking.map((projectNeed) => (
                      <span className="tag is-light" key={projectNeed}>
                        {validSeekingOptionMap[projectNeed] || projectNeed}
                      </span>
                    ))}
                  </span>
                </p>
                <p>&nbsp;</p>
              </div>

              {projectSupporters !== null && (
                <div className="block">
                  <p className="pb-1">
                    {supporterCount === 1
                      ? `${supporterCount} person supports this project!`
                      : `${supporterCount} people support this project!`}
                  </p>
                  <ProjectSupportButton
                    user={user}
                    isSupporting={isSupporting}
                    handleSupportTheProject={handleSupportTheProject}
                  />
                </div>
              )}
            </div>
          </div>

          {showGetInvolvedButton && (
            <div className="mt-4">
              <LoginProtectedButton
                className="button is-maroon is-fullwidth"
                onClick={showGetInvolvedModal}
                modalText="You must be signed in to Get Involved with a project."
                redirectUrl={`${router.asPath}?showGetInvolvedModal=true`}
              >
                Get Involved
              </LoginProtectedButton>
            </div>
          )}

          {showSampleProjectBadge && (
            <div className="mt-4">
              <div className="notification is-maroon">
                <p>Here's a sample project to help inspire new projects!</p>
              </div>
            </div>
          )}
        </div>

        <div className="column">
          <div className="tabs">
            <ul>
              {tabRoutes.map((link) => (
                <li
                  key={link.name}
                  className={link.isActive ? "is-active" : ""}
                >
                  <Link href={link.url}>{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailsCard;
